// src/pages/RegisterVehicle.tsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL;

type VehicleType =
  | "FOUR_WHEELER"
  | "SIX_WHEELER"
  | "TEN_WHEELER"
  | "TWELVE_WHEELER";

interface VehicleForm {
  vehicleNo: string;
  ownerName: string;
  ownerPhone: string;
  vehicleType: VehicleType;
  monthlyFee: string;
  openingBalance: string;
}

const RegisterVehicle: React.FC = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState<VehicleForm>({
    vehicleNo: "",
    ownerName: "",
    ownerPhone: "",
    vehicleType: "FOUR_WHEELER",
    monthlyFee: "",
    openingBalance: "",
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setShowConfirm(false);
    setLoading(true);
    setMessage("");

    try {
      const token = localStorage.getItem("token");

      const res = await fetch(`${API_URL}/vehicles/createVehicle`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          vehicleNo: form.vehicleNo,
          ownerName: form.ownerName,
          ownerPhone: form.ownerPhone,
          vehicleType: form.vehicleType,
          monthlyFee: Number(form.monthlyFee) || 0,
          balance: Number(form.openingBalance) || 0,
        }),
      });

      const data = await res.json();

      if (res.ok) {
        setMessage("✅ Vehicle registered successfully!");
        setForm({
          vehicleNo: "",
          ownerName: "",
          ownerPhone: "",
          vehicleType: "FOUR_WHEELER",
          monthlyFee: "",
          openingBalance: "",
        });
      } else {
        setMessage(`❌ Error: ${data.message || "Failed to register vehicle"}`);
      }
    } catch (err) {
      console.error(err);
      setMessage("❌ Network error. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <div className="flex items-center bg-white shadow p-4">
        <button
          onClick={() => navigate(-1)}
          className="bg-gray-200 text-gray-700 px-3 py-1 rounded hover:bg-gray-300 transition text-sm md:text-base"
        >
          Back
        </button>
        <h1 className="ml-4 text-xl md:text-2xl font-bold text-gray-800">
          Register New Vehicle
        </h1>
      </div>

      {/* Form Container */}
      <div className="flex-1 p-6 max-w-2xl w-full mx-auto">
        {message && (
          <div
            className={`mb-4 p-3 rounded ${
              message.startsWith("✅")
                ? "bg-green-100 text-green-700"
                : "bg-red-100 text-red-700"
            }`}
          >
            {message}
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setShowConfirm(true);
          }}
          className="space-y-4 bg-white shadow rounded-lg p-6"
        >
          <input
            type="text"
            name="vehicleNo"
            placeholder="Vehicle No (e.g. AS01AB1234)"
            value={form.vehicleNo}
            onChange={handleChange}
            required
            className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
          />

          <input
            type="text"
            name="ownerName"
            placeholder="Owner Name"
            value={form.ownerName}
            onChange={handleChange}
            required
            className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
          />

          <input
            type="tel"
            name="ownerPhone"
            placeholder="Owner Phone"
            value={form.ownerPhone}
            onChange={handleChange}
            required
            className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
          />

          <select
            name="vehicleType"
            value={form.vehicleType}
            onChange={handleChange}
            className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <option value="FOUR_WHEELER">4 Wheeler</option>
            <option value="SIX_WHEELER">6 Wheeler</option>
            <option value="TEN_WHEELER">10 Wheeler</option>
            <option value="TWELVE_WHEELER">12 Wheeler</option>
          </select>

          <input
            type="number"
            name="monthlyFee"
            placeholder="Monthly Fee"
            value={form.monthlyFee}
            onChange={handleChange}
            required
            className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
          />

          <input
            type="number"
            name="openingBalance"
            placeholder="Opening Balance (optional)"
            value={form.openingBalance}
            onChange={handleChange}
            className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
          />

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded hover:bg-blue-700 transition disabled:opacity-50"
          >
            {loading ? "Registering..." : "Register Vehicle"}
          </button>
        </form>

        {/* Confirmation Modal */}
        {showConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-11/12 max-w-md shadow-lg">
              <h2 className="text-xl font-bold mb-4">Confirm Vehicle Details</h2>
              <div className="space-y-2">
                <p>
                  <strong>Vehicle No:</strong> {form.vehicleNo}
                </p>
                <p>
                  <strong>Owner Name:</strong> {form.ownerName}
                </p>
                <p>
                  <strong>Owner Phone:</strong> {form.ownerPhone}
                </p>
                <p>
                  <strong>Vehicle Type:</strong> {form.vehicleType.replace("_", " ")}
                </p>
                <p>
                  <strong>Monthly Fee:</strong> ₹{form.monthlyFee}
                </p>
                <p>
                  <strong>Opening Balance:</strong> ₹{form.openingBalance || 0}
                </p>
              </div>

              <div className="mt-4 flex justify-end gap-3">
                <button
                  onClick={() => setShowConfirm(false)}
                  className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegisterVehicle;
